/**
 * Utility functions for transforming technical errors into user-friendly messages
 */
import i18n from "@/i18n";

/**
 * Transform technical error messages into user-friendly messages
 */
export function getUserFriendlyError(error: string | Error | null | undefined): string {
  if (!error) {
    return i18n.t("errors.generic", { ns: "appui" });
  }
  
  const message = typeof error === 'string' ? error : error.message;
  
  if (!message) {
    return i18n.t("errors.generic", { ns: "appui" });
  }
  
  const lowerMessage = message.toLowerCase();
  
  // Edge function errors
  if (lowerMessage.includes('non-2xx') || lowerMessage.includes('edge function')) {
    return i18n.t("errors.serverUnreachable", { ns: "appui" });
  }
  
  // Network errors
  if (lowerMessage.includes('network') || lowerMessage.includes('fetch') || lowerMessage.includes('failed to fetch')) {
    return i18n.t("errors.network", { ns: "appui" });
  }
  
  // Authentication errors
  if (lowerMessage.includes('401') || lowerMessage.includes('403') || lowerMessage.includes('unauthorized') || lowerMessage.includes('auth')) {
    return i18n.t("errors.sessionExpired", { ns: "appui" });
  }
  
  // Timeout errors
  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return i18n.t("errors.timeout", { ns: "appui" });
  }
  
  // Server errors
  if (lowerMessage.includes('500') || lowerMessage.includes('502') || lowerMessage.includes('503') || lowerMessage.includes('504')) {
    return i18n.t("errors.serverError", { ns: "appui" });
  }
  
  // Missing data errors
  if (lowerMessage.includes('missing') || lowerMessage.includes('not found')) {
    return i18n.t("errors.notFound", { ns: "appui" });
  }
  
  // Default
  return i18n.t("errors.generic", { ns: "appui" });
}

/**
 * Check if an error is retryable (transient)
 */
export function isRetryableError(error: string | Error | null | undefined): boolean {
  if (!error) return false;
  
  const message = typeof error === 'string' ? error : error.message;
  if (!message) return false;
  
  const lowerMessage = message.toLowerCase();
  
  // These errors are typically transient and worth retrying
  return (
    lowerMessage.includes('non-2xx') ||
    lowerMessage.includes('edge function') ||
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('timeout') ||
    lowerMessage.includes('503') ||
    lowerMessage.includes('502') ||
    lowerMessage.includes('504')
  );
}
